/**
 * SsoSettingsView — the "Single sign-on" settings chapter.
 *
 * Drives entirely off GET /api/sso/connection (SsoConnectionSettingsResponse):
 *
 *  - Members (the GET 403s): a quiet "ask a workspace admin" empty state.
 *  - Flag off (gates.flagEnabled === false): the form renders
 *    disabled behind a "contact support" notice — the PUT would 403 anyway.
 *  - Owner: editable IdP form (entity ID, sign-in URL, certificate, allowed
 *    domains, enabled + enforce toggles) saved via PUT /api/sso/connection.
 *  - Admin (non-owner): read-only summary of the same connection.
 *
 * When a connection row exists the response carries the SP values (ACS URL,
 * metadata URL, entity ID) the owner pastes into Okta/Entra — rendered as a
 * copy block. The IdP certificate is public key material, not a secret, so
 * echoing it in the form is safe by design.
 */
import { useCallback, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useOutletContext } from "react-router-dom";
import type {
  AuthSessionResponse,
  SsoConnectionSettingsResponse,
  WorkspaceMembershipRole,
} from "@yagni/shared";
import { Button } from "../../ui/button.js";
import { Card } from "../../ui/card.js";
import { Input } from "../../ui/input.js";
import { Label } from "../../ui/label.js";
import { Switch } from "../../ui/switch.js";
import { Textarea } from "../../ui/textarea.js";
import {
  ChapterCard,
  ChapterError,
  ChapterLede,
  ChapterLoading,
  ChapterSection,
} from "../../settings/chapter.js";
import { parseIdpMetadataXml } from "../../settings/idpMetadata.js";

interface SsoPutErrorBody {
  error?: string;
  fieldErrors?: { field: string; message: string }[];
}

/** Split the comma-separated domains input into the API's string array. */
function parseDomainsInput(text: string): string[] {
  const seen = new Set<string>();
  for (const part of text.split(",")) {
    const domain = part.trim().toLowerCase();
    if (domain.length > 0) seen.add(domain);
  }
  return [...seen];
}

/** Groups keep their case for display; the server matches case-insensitively. */
function parseGroupsInput(text: string): string[] {
  const seen = new Set<string>();
  const groups: string[] = [];
  for (const part of text.split(",")) {
    const group = part.trim();
    if (group.length === 0 || seen.has(group.toLowerCase())) continue;
    seen.add(group.toLowerCase());
    groups.push(group);
  }
  return groups;
}

const CERT_EXPIRY_WARNING_DAYS = 30;

/** Human line for a stored cert's notAfter; `expiring` within 30 days. */
function describeCertExpiry(
  notAfter: string | null | undefined,
): { label: string; expiring: boolean } | null {
  if (!notAfter) return null;
  const expiresAt = new Date(notAfter);
  if (Number.isNaN(expiresAt.getTime())) return null;
  const days = Math.floor((expiresAt.getTime() - Date.now()) / 86_400_000);
  const date = expiresAt.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  if (days < 0) return { label: `Expired ${date}.`, expiring: true };
  if (days <= CERT_EXPIRY_WARNING_DAYS) {
    return { label: `Expires ${date} (${days} day${days === 1 ? "" : "s"}).`, expiring: true };
  }
  return { label: `Expires ${date}.`, expiring: false };
}

function Field({
  label,
  htmlFor,
  hint,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <Label htmlFor={htmlFor}>{label}</Label>
      <div className="mt-1">{children}</div>
      {hint && !error && (
        <p className="mt-1 mb-0 text-xs text-[var(--text-tertiary)]">{hint}</p>
      )}
      {error && (
        <p className="mt-1 mb-0 text-xs text-[var(--error)]" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function CopyRow({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-tertiary)] font-[var(--font-mono)]">
          {label}
        </div>
        <div className="text-sm font-mono text-[var(--text-primary)] break-all">
          {value}
        </div>
      </div>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => {
          void navigator.clipboard.writeText(value);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 2000);
        }}
      >
        {copied ? "Copied" : "Copy"}
      </Button>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-baseline gap-4 px-4 py-3">
      <div className="w-44 shrink-0 text-[10px] uppercase tracking-[0.18em] text-[var(--text-tertiary)]">
        {label}
      </div>
      <div className="min-w-0 text-sm text-[var(--text-primary)] break-all">
        {value}
      </div>
    </div>
  );
}

function NotSet() {
  return <span className="text-[var(--text-tertiary)]">Not set</span>;
}

export function SsoSettingsView() {
  // Role comes from the auth session App provides on the router outlet (the
  // memberships row for the active workspace). Owner edits; admin reads;
  // member never gets here (GET 403s).
  const ctx = useOutletContext<{ session?: AuthSessionResponse } | null>();
  const session = ctx?.session ?? null;
  const role: WorkspaceMembershipRole | null =
    session?.memberships.find((m) => m.workspace.id === session.workspace.id)
      ?.role ?? null;
  const isOwner = role === "owner";

  const [data, setData] = useState<SsoConnectionSettingsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Owner form draft, seeded from every settings response (load and save).
  const [idpEntityId, setIdpEntityId] = useState("");
  const [idpSsoUrl, setIdpSsoUrl] = useState("");
  const [idpCertificate, setIdpCertificate] = useState("");
  const [idpCertificateSecondary, setIdpCertificateSecondary] = useState("");
  const [domainsText, setDomainsText] = useState("");
  const [adminGroupsText, setAdminGroupsText] = useState("");
  const [allowIdpInitiated, setAllowIdpInitiated] = useState(false);
  const [autoJoinExisting, setAutoJoinExisting] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [enforceSso, setEnforceSso] = useState(false);

  // Metadata import: paste the IdP's metadata XML, fill the fields above.
  const [metadataOpen, setMetadataOpen] = useState(false);
  const [metadataXml, setMetadataXml] = useState("");
  const [metadataNotice, setMetadataNotice] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const applyResponse = useCallback((body: SsoConnectionSettingsResponse) => {
    setData(body);
    const conn = body.connection;
    setIdpEntityId(conn?.idpEntityId ?? "");
    setIdpSsoUrl(conn?.idpSsoUrl ?? "");
    setIdpCertificate(conn?.idpCertificate ?? "");
    setIdpCertificateSecondary(conn?.idpCertificateSecondary ?? "");
    setDomainsText(conn ? conn.allowedEmailDomains.join(", ") : "");
    setAdminGroupsText(conn ? conn.adminGroups.join(", ") : "");
    setAllowIdpInitiated(conn?.allowIdpInitiated ?? false);
    setAutoJoinExisting(conn?.autoJoinExisting ?? false);
    setEnabled(conn?.enabled ?? false);
    setEnforceSso(conn?.enforceSso ?? false);
  }, []);

  const handleMetadataImport = () => {
    const parsed = parseIdpMetadataXml(metadataXml);
    if (!parsed) {
      setMetadataNotice(
        "Could not read that metadata XML. Paste the full IdP metadata document.",
      );
      return;
    }
    const filled: string[] = [];
    if (parsed.entityId) {
      setIdpEntityId(parsed.entityId);
      filled.push("entity ID");
    }
    if (parsed.ssoUrl) {
      setIdpSsoUrl(parsed.ssoUrl);
      filled.push("sign-in URL");
    }
    if (parsed.certificates.length > 0) {
      setIdpCertificate(parsed.certificates[0]!);
      filled.push("certificate");
    }
    if (parsed.certificates.length > 1) {
      setIdpCertificateSecondary(parsed.certificates[1]!);
      filled.push("standby certificate");
    }
    setMetadataNotice(
      filled.length > 0
        ? `Filled ${filled.join(", ")} from metadata. Review, then save.`
        : "That metadata document carried no usable IdP values.",
    );
  };

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch("/api/sso/connection", {
          credentials: "include",
        });
        if (cancelled) return;
        if (res.status === 403) {
          setForbidden(true);
          return;
        }
        if (!res.ok) {
          throw new Error(`Failed to load SSO settings (${res.status})`);
        }
        const body = (await res.json()) as SsoConnectionSettingsResponse;
        if (!cancelled) applyResponse(body);
      } catch (err) {
        if (!cancelled) {
          setLoadError(
            err instanceof Error ? err.message : "Failed to load SSO settings",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyResponse]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setSaveError(null);
    setFieldErrors({});
    try {
      const res = await fetch("/api/sso/connection", {
        method: "PUT",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          idpEntityId: idpEntityId.trim() || null,
          idpSsoUrl: idpSsoUrl.trim() || null,
          idpCertificate: idpCertificate.trim() || null,
          idpCertificateSecondary: idpCertificateSecondary.trim() || null,
          allowedEmailDomains: parseDomainsInput(domainsText),
          adminGroups: parseGroupsInput(adminGroupsText),
          allowIdpInitiated,
          autoJoinExisting,
          enabled,
          // Enforce is meaningless (and rejected) without SSO on.
          enforceSso: enabled && enforceSso,
        }),
      });
      if (res.status === 400) {
        const body = (await res.json().catch(() => ({}))) as SsoPutErrorBody;
        const map: Record<string, string> = {};
        for (const fe of body.fieldErrors ?? []) {
          if (!map[fe.field]) map[fe.field] = fe.message;
        }
        setFieldErrors(map);
        if (Object.keys(map).length === 0) {
          setSaveError(body.error ?? "Invalid SSO settings");
        }
        return;
      }
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as SsoPutErrorBody;
        setSaveError(body.error ?? `Save failed (${res.status})`);
        return;
      }
      const body = (await res.json()) as SsoConnectionSettingsResponse;
      applyResponse(body);
      setSaved(true);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <ChapterLoading label="Loading single sign-on..." />;
  }

  // Member role: the GET is admin-gated, so a 403 means "not yours to manage".
  if (forbidden) {
    return (
      <div data-testid="sso-member-empty">
        <p className="body-p">
          <b>Managed by your workspace admins.</b> Single sign-on (SAML) is configured by a workspace admin.
          Ask a workspace admin if your company should sign in through your identity provider.
        </p>
      </div>
    );
  }

  if (loadError || !data) {
    return <ChapterError>{loadError ?? "Failed to load SSO settings"}</ChapterError>;
  }

  const flagOff = !data.gates.flagEnabled;
  const formDisabled = flagOff || saving;
  const connection = data.connection;
  const certExpiry = describeCertExpiry(connection?.idpCertificateNotAfter);
  const standbyCertExpiry = describeCertExpiry(
    connection?.idpCertificateSecondaryNotAfter,
  );

  return (
    <div>

      {flagOff && (
        <Card tone="warning" padding="sm" className="mt-4 text-sm" data-testid="sso-flag-notice">
          SSO is not switched on for this workspace yet. Contact support to
          activate SSO for this workspace.
        </Card>
      )}

      {(certExpiry?.expiring || standbyCertExpiry?.expiring) && (
        <Card
          tone="warning"
          padding="sm"
          className="mt-4 text-sm"
          data-testid="sso-cert-expiry-notice"
        >
          {certExpiry?.expiring && (
            <>IdP signing certificate: {certExpiry.label} </>
          )}
          {standbyCertExpiry?.expiring && (
            <>Standby certificate: {standbyCertExpiry.label} </>
          )}
          Paste the renewed certificate from your identity provider before it
          lapses — sign-in stops validating once the certificate expires.
        </Card>
      )}

      {saveError && (
        <Card tone="error" padding="sm" className="mt-4 text-sm" role="alert">
          {saveError}
        </Card>
      )}

      <ChapterSection folio="01" label="Identity provider" />
      {isOwner ? (
        <>
          <ChapterLede>
            Paste these values from your identity provider's SAML application —
            or paste the IdP metadata XML once and let it fill the fields. The
            signing certificate is public key material, not a secret.
          </ChapterLede>
          <ChapterCard className="p-5" data-testid="sso-form">
            <div className="space-y-5">
              <div data-testid="sso-metadata-import">
                <button
                  type="button"
                  className="text-sm font-medium text-[var(--accent)] hover:underline"
                  onClick={() => {
                    setMetadataOpen((open) => !open);
                    setMetadataNotice(null);
                  }}
                  disabled={formDisabled}
                >
                  {metadataOpen ? "Hide metadata import" : "Import from IdP metadata XML"}
                </button>
                {metadataOpen && (
                  <div className="mt-2 space-y-2">
                    <Textarea
                      id="sso-metadata-xml"
                      aria-label="IdP metadata XML"
                      value={metadataXml}
                      onChange={(e) => setMetadataXml(e.target.value)}
                      disabled={formDisabled}
                      rows={5}
                      placeholder={'<EntityDescriptor entityID="...">...</EntityDescriptor>'}
                      className="font-mono text-xs"
                    />
                    <div className="flex items-center gap-3">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleMetadataImport}
                        disabled={formDisabled || metadataXml.trim().length === 0}
                      >
                        Fill fields from metadata
                      </Button>
                      {metadataNotice && (
                        <span className="text-xs text-[var(--text-secondary)]">
                          {metadataNotice}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <Field
                label="IdP entity ID"
                htmlFor="sso-idp-entity-id"
                error={fieldErrors.idpEntityId}
              >
                <Input
                  id="sso-idp-entity-id"
                  value={idpEntityId}
                  onChange={(e) => setIdpEntityId(e.target.value)}
                  disabled={formDisabled}
                  placeholder="https://idp.example.com/metadata"
                  state={fieldErrors.idpEntityId ? "error" : "default"}
                />
              </Field>

              <Field
                label="IdP sign-in URL"
                htmlFor="sso-idp-sso-url"
                hint="The https:// SAML sign-in (SSO) endpoint from your IdP."
                error={fieldErrors.idpSsoUrl}
              >
                <Input
                  id="sso-idp-sso-url"
                  value={idpSsoUrl}
                  onChange={(e) => setIdpSsoUrl(e.target.value)}
                  disabled={formDisabled}
                  placeholder="https://idp.example.com/sso/saml"
                  state={fieldErrors.idpSsoUrl ? "error" : "default"}
                />
              </Field>

              <Field
                label="IdP signing certificate"
                htmlFor="sso-idp-certificate"
                hint={
                  certExpiry
                    ? `X.509 signing certificate, PEM or raw base64. ${certExpiry.label}`
                    : "X.509 signing certificate, PEM or raw base64."
                }
                error={fieldErrors.idpCertificate}
              >
                <Textarea
                  id="sso-idp-certificate"
                  value={idpCertificate}
                  onChange={(e) => setIdpCertificate(e.target.value)}
                  disabled={formDisabled}
                  rows={6}
                  placeholder={"-----BEGIN CERTIFICATE-----\n..."}
                  className="font-mono text-xs"
                  state={fieldErrors.idpCertificate ? "error" : "default"}
                />
              </Field>

              <Field
                label="Standby signing certificate"
                htmlFor="sso-idp-certificate-secondary"
                hint={
                  standbyCertExpiry
                    ? `Optional. Assertions validate against either certificate, so a rollover is zero-downtime. ${standbyCertExpiry.label}`
                    : "Optional. Paste your IdP's incoming certificate here before a rollover; assertions validate against either."
                }
                error={fieldErrors.idpCertificateSecondary}
              >
                <Textarea
                  id="sso-idp-certificate-secondary"
                  value={idpCertificateSecondary}
                  onChange={(e) => setIdpCertificateSecondary(e.target.value)}
                  disabled={formDisabled}
                  rows={4}
                  placeholder={"-----BEGIN CERTIFICATE-----\n..."}
                  className="font-mono text-xs"
                  state={fieldErrors.idpCertificateSecondary ? "error" : "default"}
                />
              </Field>

              <Field
                label="Allowed email domains"
                htmlFor="sso-domains"
                hint="Comma-separated. Only assertions for these domains can sign in."
                error={fieldErrors.allowedEmailDomains}
              >
                <Input
                  id="sso-domains"
                  value={domainsText}
                  onChange={(e) => setDomainsText(e.target.value)}
                  disabled={formDisabled}
                  placeholder="example.com, example.io"
                  state={fieldErrors.allowedEmailDomains ? "error" : "default"}
                />
              </Field>

              <Field
                label="Admin groups"
                htmlFor="sso-admin-groups"
                hint="Comma-separated IdP group names. Users in any of these sign in as workspace admins; everyone else lands as member. Leave empty to manage roles by hand. Owners are never changed by SSO."
                error={fieldErrors.adminGroups}
              >
                <Input
                  id="sso-admin-groups"
                  value={adminGroupsText}
                  onChange={(e) => setAdminGroupsText(e.target.value)}
                  disabled={formDisabled}
                  placeholder="yagni-admins"
                  state={fieldErrors.adminGroups ? "error" : "default"}
                />
              </Field>

              <div className="border-t border-[var(--border-subtle)] pt-4 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm font-medium text-[var(--text-primary)]">
                      Allow IdP-initiated sign-in
                    </div>
                    <div className="text-xs text-[var(--text-secondary)]">
                      Let your team launch YAGNI from the identity provider's
                      app tile (Okta dashboard). Off = sign-in always starts
                      from this app.
                    </div>
                  </div>
                  <Switch
                    aria-label="Allow IdP-initiated sign-in"
                    checked={allowIdpInitiated}
                    onCheckedChange={setAllowIdpInitiated}
                    disabled={formDisabled}
                  />
                </div>

                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm font-medium text-[var(--text-primary)]">
                      Auto-join existing accounts
                    </div>
                    <div className="text-xs text-[var(--text-secondary)]">
                      An existing YAGNI account at an allowed domain joins this
                      workspace on SSO sign-in instead of needing an invite
                      first.
                    </div>
                  </div>
                  <Switch
                    aria-label="Auto-join existing accounts"
                    checked={autoJoinExisting}
                    onCheckedChange={setAutoJoinExisting}
                    disabled={formDisabled}
                  />
                </div>

                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm font-medium text-[var(--text-primary)]">
                      SSO enabled
                    </div>
                    <div className="text-xs text-[var(--text-secondary)]">
                      Let this workspace sign in through the identity provider
                      above.
                    </div>
                    {fieldErrors.enabled && (
                      <p className="mt-1 mb-0 text-xs text-[var(--error)]" role="alert">
                        {fieldErrors.enabled}
                      </p>
                    )}
                  </div>
                  <Switch
                    aria-label="SSO enabled"
                    checked={enabled}
                    onCheckedChange={(v) => {
                      setEnabled(v);
                      if (!v) setEnforceSso(false);
                    }}
                    disabled={formDisabled}
                  />
                </div>

                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm font-medium text-[var(--text-primary)]">
                      Require SSO for members
                    </div>
                    <div className="text-xs text-[var(--text-secondary)]">
                      Members must use SSO to sign in. Owners and admins keep
                      Google sign-in as break-glass access.
                    </div>
                    {fieldErrors.enforceSso && (
                      <p className="mt-1 mb-0 text-xs text-[var(--error)]" role="alert">
                        {fieldErrors.enforceSso}
                      </p>
                    )}
                  </div>
                  <Switch
                    aria-label="Require SSO for members"
                    checked={enforceSso}
                    onCheckedChange={setEnforceSso}
                    disabled={formDisabled || !enabled}
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 pt-1">
                <Button
                  onClick={() => void handleSave()}
                  disabled={formDisabled}
                  loading={saving}
                >
                  Save
                </Button>
                {saved && (
                  <span className="text-xs text-[var(--success)]">Saved.</span>
                )}
              </div>
            </div>
          </ChapterCard>
        </>
      ) : (
        <>
          <ChapterLede>
            Only the workspace owner can change SSO settings.
          </ChapterLede>
          <ChapterCard data-testid="sso-readonly">
            <div className="divide-y divide-[var(--border-subtle)]">
              <SummaryRow
                label="IdP entity ID"
                value={connection?.idpEntityId ?? <NotSet />}
              />
              <SummaryRow
                label="IdP sign-in URL"
                value={connection?.idpSsoUrl ?? <NotSet />}
              />
              <SummaryRow
                label="Signing certificate"
                value={
                  connection?.idpCertificate ? (
                    certExpiry ? `On file. ${certExpiry.label}` : "On file"
                  ) : (
                    <NotSet />
                  )
                }
              />
              <SummaryRow
                label="Standby certificate"
                value={
                  connection?.idpCertificateSecondary ? (
                    standbyCertExpiry
                      ? `On file. ${standbyCertExpiry.label}`
                      : "On file"
                  ) : (
                    <NotSet />
                  )
                }
              />
              <SummaryRow
                label="Allowed email domains"
                value={
                  connection && connection.allowedEmailDomains.length > 0 ? (
                    connection.allowedEmailDomains.join(", ")
                  ) : (
                    <NotSet />
                  )
                }
              />
              <SummaryRow
                label="Admin groups"
                value={
                  connection && connection.adminGroups.length > 0 ? (
                    connection.adminGroups.join(", ")
                  ) : (
                    <NotSet />
                  )
                }
              />
              <SummaryRow
                label="IdP-initiated sign-in"
                value={connection?.allowIdpInitiated ? "On" : "Off"}
              />
              <SummaryRow
                label="Auto-join existing accounts"
                value={connection?.autoJoinExisting ? "On" : "Off"}
              />
              <SummaryRow
                label="SSO enabled"
                value={connection?.enabled ? "On" : "Off"}
              />
              <SummaryRow
                label="Require SSO for members"
                value={connection?.enforceSso ? "On" : "Off"}
              />
            </div>
          </ChapterCard>
        </>
      )}

      <ChapterSection folio="02" label="Give these to your identity provider" />
      {data.serviceProvider ? (
        <>
          <ChapterLede>
            Paste these service-provider values into your IdP's SAML
            application (Okta, Entra, Google Workspace).
          </ChapterLede>
          <ChapterCard data-testid="sso-sp-block">
            <div className="divide-y divide-[var(--border-subtle)]">
              <CopyRow label="ACS URL" value={data.serviceProvider.acsUrl} />
              <CopyRow
                label="Metadata URL"
                value={data.serviceProvider.metadataUrl}
              />
              <CopyRow
                label="Entity ID"
                value={data.serviceProvider.entityId}
              />
            </div>
          </ChapterCard>
        </>
      ) : (
        <ChapterLede>
          Save your identity provider details once to generate this workspace's
          service-provider URLs.
        </ChapterLede>
      )}
    </div>
  );
}
